import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Slack, Upload, Check, AlertCircle, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import GoogleCalendarConnect from "@/components/integrations/GoogleCalendarConnect";
import SlackConnect from "@/components/integrations/SlackConnect";

export default function Integrations() {
  const integrations = [
    {
      id: "csv-import",
      name: "CSV Import",
      description: "Importer prosjekter og oppgaver fra CSV-filer",
      icon: Upload,
      status: "available",
      color: "from-emerald-500 to-emerald-600",
      benefits: [
        "Rask import av flere prosjekter samtidig",
        "Importer oppgaver med all informasjon",
        "Enkelt å migrere data fra andre systemer"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">Integrasjoner</h1>
          <p className="text-slate-500">Koble PP-X til dine favorittverktøy</p>
        </div>

        {/* Backend Functions Notice */}
        <Card className="p-6 bg-gradient-to-br from-lime-100 to-blue-50 border-lime-200 mb-6">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-lime-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-zinc-900" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-2">Slik aktiverer du Google Calendar & Slack</h3>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">1</span>
                  <div>
                    <p className="font-medium text-slate-900">Gå til Base44 Dashboard</p>
                    <p>Åpne Base44 dashboard i en ny fane</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">2</span>
                  <div>
                    <p className="font-medium text-slate-900">Aktiver Backend Functions</p>
                    <p>Gå til Settings for denne appen og aktiver "Backend Functions"</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">3</span>
                  <div>
                    <p className="font-medium text-slate-900">Koble til Google Calendar</p>
                    <p>Når backend functions er aktivert, kan jeg sette opp OAuth-tilkoblingen for deg</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">4</span>
                  <div>
                    <p className="font-medium text-slate-900">Automatisk synkronisering</p>
                    <p>Oppgaver med forfallsdatoer vil automatisk synkroniseres til din Google Calendar</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white rounded-lg border border-lime-200">
                <p className="text-xs text-slate-600">
                  <strong>Merk:</strong> Backend Functions krever en betalt Base44-plan. 
                  Etter aktivering, kom tilbake hit så setter jeg opp integrasjonene for deg.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Google Calendar Integration */}
        <GoogleCalendarConnect />

        {/* Slack Integration */}
        <SlackConnect />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {integrations.map((integration) => {
            const Icon = integration.icon;
            const isAvailable = integration.status === "available";

            return (
              <Card key={integration.id} className="bg-white border-0 shadow-sm overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${integration.color}`} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${integration.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <Badge className={isAvailable ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {isAvailable ? "Tilgjengelig" : "Krever Backend"}
                    </Badge>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {integration.name}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {integration.description}
                  </p>

                  <div className="space-y-2 mb-6">
                    {integration.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {isAvailable ? (
                    <Link to={createPageUrl("ImportData")}>
                      <Button className="w-full bg-slate-900 hover:bg-slate-800">
                        Åpne Import
                      </Button>
                    </Link>
                  ) : (
                    <Button disabled className="w-full">
                      Aktivér Backend Functions
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Documentation */}
        <Card className="mt-8 p-6 bg-white border-0 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Hvordan aktivere integrasjoner?</h3>
              <div className="space-y-2 text-sm text-slate-600">
                <p><strong>1. CSV Import:</strong> Denne er allerede klar til bruk! Klikk på "Åpne Import" over.</p>
                <p><strong>2. Google Calendar & Slack:</strong> For å aktivere disse integrasjonene:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Gå til Base44 Dashboard</li>
                  <li>Velg Settings for denne appen</li>
                  <li>Aktiver "Backend Functions"</li>
                  <li>Kom tilbake hit for å sette opp OAuth-tilkoblinger</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}